'use client';

import React, { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import { useEnquiries } from '@/hooks/useEnquiries';
import { useConcierge } from '@/hooks/useConcierge';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import {
  MessageSquare,
  MoreHorizontal,
  Sparkles,
  Search,
  Filter,
  Paperclip,
  Send,
  ChevronLeft,
  MapPin,
  Phone,
  Loader2,
  User,
} from 'lucide-react';
import type { Enquiry, AssignedAgent } from '@/types/customer';
import { cn } from '@/lib/utils';

export const Enquiries: React.FC = () => {
  const { enquiries, isLoading, sendMessage } = useEnquiries();
  const { agent } = useConcierge();
  const [selectedEnquiryId, setSelectedEnquiryId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Set first enquiry as active initially if available
  useEffect(() => {
    const firstEnquiry = enquiries[0];
    if (firstEnquiry && !selectedEnquiryId) {
      setSelectedEnquiryId(firstEnquiry.id);
    }
  }, [enquiries, selectedEnquiryId]);

  const activeEnquiry = enquiries.find((e) => e.id === selectedEnquiryId);

  const filteredEnquiries = enquiries.filter(
    (e) =>
      e.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.propertyId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-140px)] min-h-[600px] animate-[fadeIn_0.3s_ease-out] flex flex-col md:flex-row gap-6 pb-6">
      {/* LEFT COLUMN: Inbox List */}
      <div
        className={cn(
          'flex-col w-full md:w-1/3 lg:w-1/4 bg-white rounded-3xl border border-stone-200 shadow-sm overflow-hidden flex',
          selectedEnquiryId ? 'hidden md:flex' : 'flex'
        )}
      >
        {/* Inbox Header */}
        <div className="p-4 border-b border-stone-100 bg-stone-50/50">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-stone-900 tracking-tight">Messages</h2>
            <button className="p-2 text-stone-400 hover:text-stone-900 transition-colors">
              <Filter size={18} />
            </button>
          </div>
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
            />
            <input
              type="text"
              placeholder="Search enquiries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400 transition-all"
            />
          </div>
        </div>

        {/* Thread List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {filteredEnquiries.map((enquiry) => (
            <EnquiryListItem
              key={enquiry.id}
              enquiry={enquiry}
              isActive={selectedEnquiryId === enquiry.id}
              onClick={() => setSelectedEnquiryId(enquiry.id)}
            />
          ))}
          {filteredEnquiries.length === 0 && (
            <div className="text-center py-10 px-4">
              <p className="text-stone-400 text-sm">No enquiries found.</p>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT COLUMN: Chat Interface */}
      <div
        className={cn(
          'flex-1 bg-white rounded-3xl border border-stone-200 shadow-sm overflow-hidden flex flex-col relative',
          !selectedEnquiryId ? 'hidden md:flex' : 'flex'
        )}
      >
        {activeEnquiry ? (
          <ChatInterface
            enquiry={activeEnquiry}
            agent={agent}
            onBack={() => setSelectedEnquiryId(null)}
            onSendMessage={sendMessage}
          />
        ) : (
          <EmptyChatState />
        )}
      </div>
    </div>
  );
};

// --- SUB-COMPONENT: Inbox List Item ---
const EnquiryListItem: React.FC<{
  enquiry: Enquiry;
  isActive: boolean;
  onClick: () => void;
}> = ({ enquiry, isActive, onClick }) => {
  const lastMessage = enquiry.messages?.[enquiry.messages.length - 1];
  const hasUnread = enquiry.status === 'IN_PROGRESS';

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left p-3 rounded-2xl transition-all duration-200 group flex gap-3 relative overflow-hidden',
        isActive ? 'bg-stone-900 text-white shadow-md' : 'hover:bg-stone-50 text-stone-900'
      )}
    >
      {/* Property Image Placeholder */}
      <div className="relative shrink-0">
        <div
          className={cn(
            'w-12 h-12 rounded-xl bg-gradient-to-br from-stone-300 to-stone-500 border',
            isActive ? 'border-stone-700' : 'border-stone-200'
          )}
        />
        {hasUnread && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 border-2 border-white rounded-full flex items-center justify-center text-[8px] font-bold text-white">
            !
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex justify-between items-start mb-0.5">
          <h4
            className={cn(
              'font-semibold text-sm truncate pr-2',
              isActive ? 'text-stone-100' : 'text-stone-900'
            )}
          >
            {enquiry.subject}
          </h4>
          <span className={cn('text-[10px] whitespace-nowrap', 'text-stone-400')}>
            {new Date(enquiry.createdAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
            })}
          </span>
        </div>

        <p className={cn('text-xs truncate', isActive ? 'text-stone-400' : 'text-stone-500')}>
          {lastMessage?.content || 'No messages yet'}
        </p>

        <div className="flex items-center gap-1 mt-1.5">
          <span
            className={cn(
              'text-[10px] flex items-center gap-1',
              isActive ? 'text-stone-500' : 'text-stone-400'
            )}
          >
            <Badge
              variant={
                enquiry.status === 'RESOLVED'
                  ? 'success'
                  : enquiry.status === 'IN_PROGRESS'
                    ? 'warning'
                    : 'neutral'
              }
              className="text-[8px]"
            >
              {enquiry.status}
            </Badge>
          </span>
        </div>
      </div>
    </button>
  );
};

// --- SUB-COMPONENT: Chat Interface ---
const ChatInterface: React.FC<{
  enquiry: Enquiry;
  agent: AssignedAgent | null;
  onBack: () => void;
  onSendMessage: (enquiryId: string, content: string) => Promise<Enquiry | null>;
}> = ({ enquiry, agent, onBack, onSendMessage }) => {
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom on load/new message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [enquiry.messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isSending) return;

    setIsSending(true);
    try {
      await onSendMessage(enquiry.id, newMessage);
      setNewMessage('');
    } finally {
      setIsSending(false);
    }
  };

  const smartReplies = [
    'Is this property still available?',
    "I'd like to book a viewing.",
    'What are the service charges?',
    'Please send the floorplan.',
  ];

  return (
    <div className="flex flex-col h-full bg-stone-50/30">
      {/* Header: Property Context */}
      <div className="bg-white border-b border-stone-200 p-4 shadow-sm z-10 flex flex-col gap-4">
        {/* Mobile Back Button & Title */}
        <div className="flex items-center gap-2 md:hidden">
          <button
            onClick={onBack}
            className="p-2 -ml-2 text-stone-500 hover:bg-stone-100 rounded-full"
          >
            <ChevronLeft size={24} />
          </button>
          <span className="font-semibold text-stone-900 truncate">{enquiry.subject}</span>
        </div>

        {/* Enquiry Info */}
        <div className="bg-stone-50 rounded-xl p-3 border border-stone-100 flex items-center justify-between group cursor-pointer hover:bg-white hover:border-stone-300 transition-all">
          <div className="flex items-center gap-4">
            <div className="w-16 h-12 rounded-lg bg-gradient-to-br from-stone-300 to-stone-500 shadow-sm" />
            <div>
              <h3 className="font-bold text-stone-900 text-sm">{enquiry.subject}</h3>
              <p className="text-xs text-stone-500 flex items-center gap-1">
                <MapPin size={10} /> Property Enquiry
              </p>
            </div>
          </div>
          <div className="text-right">
            <Badge
              variant={
                enquiry.status === 'RESOLVED'
                  ? 'success'
                  : enquiry.status === 'IN_PROGRESS'
                    ? 'warning'
                    : 'neutral'
              }
            >
              {enquiry.status}
            </Badge>
          </div>
        </div>

        {/* Agent Info */}
        <div className="flex justify-between items-center px-1">
          <div className="flex items-center gap-3">
            <div className="relative">
              {agent?.avatarUrl ? (
                <Image
                  src={agent.avatarUrl}
                  width={32}
                  height={32}
                  className="w-8 h-8 rounded-full border border-stone-200 object-cover"
                  alt={agent.name || 'Agent'}
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-stone-200 border border-stone-300 flex items-center justify-center">
                  <User size={14} className="text-stone-500" />
                </div>
              )}
              {agent?.isOnline && (
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full" />
              )}
            </div>
            <div>
              <p className="text-xs font-bold text-stone-900">{agent?.name || 'Agent'}</p>
              <p className="text-[10px] text-stone-500 uppercase tracking-wider">
                {agent?.isOnline ? 'Online Now' : agent?.title || 'Property Advisor'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {agent?.phone ? (
              <a href={`tel:${agent.phone}`}>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 rounded-full bg-white border-stone-200 text-stone-500 hover:text-stone-900"
                >
                  <Phone size={14} />
                </Button>
              </a>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 rounded-full bg-white border-stone-200 text-stone-400 opacity-50 cursor-not-allowed"
                disabled
              >
                <Phone size={14} />
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 rounded-full bg-white border-stone-200 text-stone-500"
            >
              <MoreHorizontal size={14} />
            </Button>
          </div>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6" ref={scrollRef}>
        {/* AI Notice */}
        <div className="flex justify-center">
          <span className="text-[10px] text-stone-400 bg-stone-100 px-3 py-1 rounded-full flex items-center gap-1">
            <Sparkles size={10} /> AI Assistant Active
          </span>
        </div>

        {enquiry.messages?.map((msg) => (
          <div
            key={msg.id}
            className={cn('flex', msg.senderType === 'CUSTOMER' ? 'justify-end' : 'justify-start')}
          >
            <div
              className={cn(
                'max-w-[80%] flex flex-col',
                msg.senderType === 'CUSTOMER' ? 'items-end' : 'items-start'
              )}
            >
              <div
                className={cn(
                  'p-4 rounded-2xl text-sm relative shadow-sm',
                  msg.senderType === 'CUSTOMER'
                    ? 'bg-stone-900 text-white rounded-tr-none'
                    : 'bg-white text-stone-800 border border-stone-200 rounded-tl-none'
                )}
              >
                {msg.content}
              </div>
              <span className="text-[10px] text-stone-400 mt-1 px-1">
                {new Date(msg.createdAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Smart Replies */}
      {enquiry.messages &&
        enquiry.messages.length > 0 &&
        enquiry.messages[enquiry.messages.length - 1]?.senderType === 'AGENT' && (
          <div className="px-4 py-2 flex gap-2 overflow-x-auto">
            <button
              className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 text-indigo-400 text-xs font-medium rounded-full whitespace-nowrap cursor-not-allowed opacity-60"
              disabled
              title="AI suggestions coming soon"
            >
              <Sparkles size={12} /> Suggest Reply
            </button>
            {smartReplies.map((reply, i) => (
              <button
                key={i}
                onClick={() => setNewMessage(reply)}
                className="px-3 py-1.5 bg-white border border-stone-200 text-stone-600 text-xs font-medium rounded-full hover:bg-stone-50 hover:border-stone-300 whitespace-nowrap transition-all"
              >
                {reply}
              </button>
            ))}
          </div>
        )}

      {/* Input Area */}
      <div className="p-4 bg-white border-t border-stone-200">
        <div className="flex items-end gap-2 bg-stone-50 border border-stone-200 rounded-2xl p-2 focus-within:ring-2 focus-within:ring-stone-900/10 focus-within:border-stone-400 transition-all">
          <button
            className="p-2 text-stone-300 rounded-xl cursor-not-allowed"
            disabled
            title="File attachments coming soon"
          >
            <Paperclip size={20} />
          </button>
          <textarea
            rows={1}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 bg-transparent border-none focus:ring-0 p-2 text-sm text-stone-900 placeholder:text-stone-400 resize-none max-h-32 outline-none"
            onInput={(e) => {
              e.currentTarget.style.height = 'auto';
              e.currentTarget.style.height = e.currentTarget.scrollHeight + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
          />
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || isSending}
            className="p-2 bg-stone-900 text-white rounded-xl hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
          >
            {isSending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
};

const EmptyChatState = () => (
  <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-stone-50/50">
    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-sm border border-stone-100 mb-6">
      <MessageSquare size={32} className="text-stone-300" />
    </div>
    <h3 className="text-xl font-bold text-stone-900 mb-2">Select a Conversation</h3>
    <p className="text-stone-500 max-w-sm mb-8">
      Choose an enquiry from the list to view details and contact the agent.
    </p>
  </div>
);
